/* eslint-disable  @typescript-eslint/no-explicit-any */ // AJB: remove
import BREED_OPTIONS from "@/constants/BreedOptions";
import COLOUR_OPTIONS from "@/constants/ColorOptions";
import DISCIPLINE_OPTIONS from "@/constants/DisciplineOptions";
import GENDER_OPTIONS from "@/constants/GenderOptions";
import RIDER_LEVEL_OPTIONS from "@/constants/RiderLevelOptions";
import STALLION_BREED_OPTIONS from "@/constants/StallionBreedOptions";
import VERTICAL_OPTIONS from "@/constants/VerticalOptions";
import CATEGORY_HIERARCHY, {
  FlattenCategories,
} from "@/constants/CategoryHierarchy";
import { AdRangeAspectKey } from "@/models/AdRangeAspectKey";
import { AdValueAspectKey } from "@/models/AdValueAspectKey";
import HomePageSearchRequest from "@/models/HomePageSearchRequest";
import {
  AreaSearch,
  FlatAspect,
  RangeAspect,
} from "@/models/HomePageSearchResponse";
import LOCATION_HEIRARCHY, {
  FlattenLocations,
} from "@/constants/LocationHierarchy";
import { AdConditionAspectKey } from "@/models/AdConditionAspectKey";
import { AdSortOrderKey } from "./AdSortService";

const LOCATION_OPTIONS = FlattenLocations(LOCATION_HEIRARCHY);

interface AdValueAspectConfig {
  key: AdValueAspectKey;
  options: FlatAspect[];
}

const AD_VALUE_ASPECT_CONFIGS: AdValueAspectConfig[] = [
  {
    key: "vertical",
    options: VERTICAL_OPTIONS,
  },
  // AJB: these are only horses for sale breeds, not stallion breeds, should really just make it categories?
  {
    key: "breeds",
    options: [...BREED_OPTIONS, ...STALLION_BREED_OPTIONS],
  },
  {
    key: "categories",
    options: FlattenCategories(
      CATEGORY_HIERARCHY.filter((x) => !x.isHorses).flatMap((x) => x.children)
    ),
  },
  {
    key: "disciplines",
    options: DISCIPLINE_OPTIONS,
  },
  {
    key: "locations",
    options: LOCATION_OPTIONS,
  },
  {
    key: "colours",
    options: COLOUR_OPTIONS,
  },
  {
    key: "genders",
    options: GENDER_OPTIONS,
  },
  {
    key: "riderLevels",
    options: RIDER_LEVEL_OPTIONS,
  },
];

const ALL_VALUE_ASPECT_CONFIGS_BY_SLUG = AD_VALUE_ASPECT_CONFIGS.reduce(
  (acc, segment) => {
    segment.options.forEach((option) => {
      if (option.slug) {
        if (acc[option.slug]) {
          throw new Error(`Duplicate slug: ${option.slug}`);
        }
        acc[option.slug] = {
          option: option,
          segment: segment,
        };
      }
    });
    return acc;
  },
  {} as { [slug: string]: { option: FlatAspect; segment: AdValueAspectConfig } }
);

interface AdRangeAspectConfig {
  key: AdRangeAspectKey;
  suffix: string;
  singularSuffix?: string;
}

const AD_RANGE_ASPECT_CONFIGS: AdRangeAspectConfig[] = [
  {
    key: "price",
    suffix: "",
  },
  {
    key: "height",
    suffix: "-hands-high",
  },
  {
    key: "age",
    suffix: "-years-old",
    singularSuffix: "-year-old",
  },
];

const LOWER_BOUND_PREFIX = "over-";
const UPPER_BOUND_PREFIX = "under-";
const FULLY_BOUND_PREFIX = "between-";

interface AdConditionAspectConfig {
  key: AdConditionAspectKey;
  slug: string;
}

const AD_CONDITION_ASPECT_CONFIGS: AdConditionAspectConfig[] = [
  {
    key: "isUrgent",
    slug: "urgent-only",
  },
];

const PREFIXED_SLUGS = [
  {
    prefix: "keyword-",
    key: "searchQuery",
    get: (request: HomePageSearchRequest) => request.searchQuery,
    set: (request: HomePageSearchRequest, value: string) =>
      (request.searchQuery = decodeURIComponent(value)),
  },
  {
    prefix: "page-",
    key: "page",
    get: (request: HomePageSearchRequest) =>
      request.page > 1 ? request.page : null,
    set: (request: HomePageSearchRequest, value: string) =>
      (request.page = parseInt(value)),
    excludeFromCanonical: true,
  },
  {
    prefix: "sort-",
    get: (request: HomePageSearchRequest) =>
      request.sortBy !== "most-recent" ? request.sortBy : null,
    set: (request: HomePageSearchRequest, value: string) =>
      (request.sortBy = value as AdSortOrderKey),
    excludeFromCanonical: true,
  },
  {
    prefix: "area-",
    get: (request: HomePageSearchRequest) => {
      const area = request.area;
      if (area.label) {
        return `${area.lat}|${area.lon}|${area.distance}|${area.label}`;
      }
      return null;
    },
    set: (request: HomePageSearchRequest, value: string) => {
      const areaParts = value.split("|");
      request.area = new AreaSearch();
      if (areaParts.length === 4) {
        request.area.lat = parseFloat(areaParts[0]);
        request.area.lon = parseFloat(areaParts[1]);
        request.area.distance = areaParts[2];
        request.area.label = decodeURIComponent(areaParts[3]);
      }
    },
  },
];

export function BuildUrl(
  request: HomePageSearchRequest,
  canonical?: boolean
): string {
  let url = "/for-sale";

  const aspectSegments = AD_VALUE_ASPECT_CONFIGS.map((config) => {
    // AJB: cleaner way to resolve type?
    const selectionsRaw = request[config.key] as any;
    if (!selectionsRaw) {
      return null;
    }
    const selections = Array.isArray(selectionsRaw)
      ? selectionsRaw
      : [selectionsRaw];
    const selectionIds = selections.map((x: FlatAspect) => x.id);
    const options = config.options.filter((x) => selectionIds.includes(x.id));
    return options.map((x) => x.slug).join("/");
  }).filter((x) => x);

  if (aspectSegments.length > 0) {
    url += "/" + aspectSegments.join("/");
  }

  const rangeSegments = AD_RANGE_ASPECT_CONFIGS.map((config) => {
    // AJB: tighten types
    const value = request[config.key] as unknown as RangeAspect;
    let result;
    let chosenSuffix;
    if (value.min > -1 && value.max > -1) {
      result = `${FULLY_BOUND_PREFIX}${value.min}-and-${value.max}`;
      chosenSuffix = config.suffix;
    } else if (value.min > -1) {
      result = `${LOWER_BOUND_PREFIX}${value.min}`;
      chosenSuffix =
        value.min === 1 && config.singularSuffix
          ? config.singularSuffix
          : config.suffix;
    } else if (value.max > -1) {
      result = `${UPPER_BOUND_PREFIX}${value.max}`;
      chosenSuffix =
        value.max === 1 && config.singularSuffix
          ? config.singularSuffix
          : config.suffix;
    }
    if (result) {
      if (chosenSuffix) {
        result += chosenSuffix;
      }
      return result;
    }
  }).filter((x) => x);

  if (rangeSegments.length > 0) {
    url += "/" + rangeSegments.join("/");
  }

  const conditionSegments = AD_CONDITION_ASPECT_CONFIGS.map((config) => {
    if (request[config.key]) {
      return config.slug;
    }
  }).filter((x) => x);

  if (conditionSegments.length > 0) {
    url += "/" + conditionSegments.join("/");
  }

  PREFIXED_SLUGS.filter((x) => !x.excludeFromCanonical || !canonical).forEach(
    ({ prefix, get }) => {
      const value = get(request);
      if (value) {
        url += `/${prefix}${encodeURIComponent(value)}`;
      }
    }
  );

  return url;
}

// AJB: if this is used in the client the file structure may not be ideal?
export function ParseUrl(slugs: string[]): HomePageSearchRequest {
  const request = new HomePageSearchRequest();

  slugs.forEach((slug) => {
    if (!slug) {
      return;
    }

    const optionBySlug = ALL_VALUE_ASPECT_CONFIGS_BY_SLUG[slug];
    if (optionBySlug) {
      // AJB: tighten types
      if (Array.isArray(request[optionBySlug.segment.key])) {
        (request[optionBySlug.segment.key] as any).push(optionBySlug.option);
      } else {
        request[optionBySlug.segment.key] = optionBySlug.option as any;
      }
      return;
    }

    const matchedConditon = AD_CONDITION_ASPECT_CONFIGS.find(
      (config) => config.slug === slug
    );
    if (matchedConditon) {
      request[matchedConditon.key] = true;
      return;
    }

    const prefixedSlugConfig = PREFIXED_SLUGS.find(({ prefix }) =>
      slug.startsWith(prefix)
    );
    if (prefixedSlugConfig) {
      let cleansedText = slug.replace(prefixedSlugConfig.prefix, "");
      cleansedText = decodeURIComponent(cleansedText);
      prefixedSlugConfig.set(request, cleansedText);
    }

    const value = new RangeAspect();
    let extractedSuffix: string | null = null;
    if (slug.startsWith(LOWER_BOUND_PREFIX)) {
      const lowerBoundMatch = slug.match(
        new RegExp(`^${LOWER_BOUND_PREFIX}(\\d*\\.?\\d?)(.*)$`)
      );
      if (lowerBoundMatch) {
        value.min = parseFloat(lowerBoundMatch[1]);
        extractedSuffix = lowerBoundMatch[2];
      }
    } else if (slug.startsWith(UPPER_BOUND_PREFIX)) {
      const upperBoundMatch = slug.match(
        new RegExp(`^${UPPER_BOUND_PREFIX}(\\d*\\.?\\d?)(.*)$`)
      );
      if (upperBoundMatch) {
        value.max = parseFloat(upperBoundMatch[1]);
        extractedSuffix = upperBoundMatch[2];
      }
    } else if (slug.startsWith(FULLY_BOUND_PREFIX)) {
      const fullyBoundMatch = slug.match(
        new RegExp(`^${FULLY_BOUND_PREFIX}(\\d*\\.?\\d?)-and-(\\d*\\.?\\d?)(.*)$`)
      );
      if (fullyBoundMatch) {
        value.min = parseFloat(fullyBoundMatch[1]);
        value.max = parseFloat(fullyBoundMatch[2]);
        extractedSuffix = fullyBoundMatch[3];
      }
    }
    const matchedConfig =
      extractedSuffix !== null &&
      AD_RANGE_ASPECT_CONFIGS.find(
        (config) =>
          config.suffix === extractedSuffix ||
          (config.singularSuffix && config.singularSuffix === extractedSuffix)
      );
    if (matchedConfig) {
      request[matchedConfig.key] = value;
    }
  });

  return request;
}
